import { useEffect, useRef, useState } from 'react';
import { create } from 'zustand';

import type { EntityDesc } from '../../lib/scene-doc.js';
import { getView } from '../../engine/prism.js';
import { docIO } from '../../lib/doc-io.js';
import { compositeFrame, parsePixelDoc, sliceSheet } from '../../lib/pixel-doc.js';
import { useWorkspace } from '../../state/workspace.js';
import { readImagePixels } from '../pixel/pixel-io.js';
import { IconButton } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';

/** Editor tile brush, mirrored into the scene view (`tileBrush` option). */
interface TileBrushState {
  /** Stable id of the tilemap being painted (null = not painting). */
  painting: string | null;
  layer: number;
  tile: number;
  size: number;
  set: (patch: Partial<Omit<TileBrushState, 'set'>>) => void;
}

export const useTileBrush = create<TileBrushState>((set, get) => ({
  painting: null,
  layer: 0,
  tile: 0,
  size: 1,
  set: (patch) => {
    set(patch);
    const s = get();
    getView('scene')?.setOption('tileBrush', s.painting ? JSON.stringify({ layer: s.layer, tile: s.tile, size: s.size }) : '');
  },
}));

interface TileImages {
  key: string;
  tiles: Array<{ pixels: Uint8ClampedArray; w: number; h: number }>;
}

/** Loads a tileset (`.pixel.json` frames or a PNG cut into tiles). */
async function loadTiles(projectId: number, path: string, tw: number, th: number): Promise<TileImages['tiles']> {
  if (path.endsWith('.pixel.json')) {
    const doc = parsePixelDoc(await docIO().read(projectId, path));
    return doc.cells.map((_, i) => ({ pixels: compositeFrame(doc, i), w: doc.width, h: doc.height }));
  }
  const img = await readImagePixels(projectId, path);
  return sliceSheet(img.pixels, img.width, img.height, tw, th).map((pixels) => ({ pixels, w: tw, h: th }));
}

function TileThumb({ tile }: { tile: TileImages['tiles'][number] }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    c.width = tile.w;
    c.height = tile.h;
    c.getContext('2d')!.putImageData(new ImageData(new Uint8ClampedArray(tile.pixels), tile.w, tile.h), 0, 0);
  }, [tile]);
  return <canvas ref={ref} className="tile-thumb" />;
}

interface LayerJson {
  name?: string;
  data?: number[];
  solid?: boolean;
  visible?: boolean;
  [k: string]: unknown;
}

/** Tile palette, layers and map size for a selected `tilemap`. */
export function TilemapEditor({
  entity,
  value,
  onChange,
}: {
  entity: EntityDesc;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const brush = useTileBrush();
  const projectId = useWorkspace((s) => s.projectId);
  const tileset = typeof value.tileset === 'string' ? value.tileset : '';
  const tileSize = Array.isArray(value.tileSize) ? (value.tileSize as number[]) : [16, 16];
  const size = Array.isArray(value.size) ? (value.size as number[]) : [0, 0];
  const layers = (Array.isArray(value.layers) ? value.layers : []) as LayerJson[];
  const [images, setImages] = useState<TileImages | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sizeText, setSizeText] = useState<string | null>(null);
  const painting = brush.painting === entity.id;
  const key = `${tileset}:${tileSize[0]}x${tileSize[1]}`;

  useEffect(() => {
    if (!projectId || !tileset) {
      setImages(null);
      return;
    }
    let live = true;
    loadTiles(projectId, tileset, tileSize[0] ?? 16, tileSize[1] ?? 16)
      .then((tiles) => live && (setImages({ key, tiles }), setError(null)))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [projectId, key]);

  // Stop painting when this map is deselected.
  useEffect(
    () => () => {
      if (useTileBrush.getState().painting === entity.id) useTileBrush.getState().set({ painting: null });
    },
    [entity.id],
  );

  const setLayers = (next: LayerJson[]) => onChange({ ...value, layers: next });
  const resize = (w: number, h: number) => {
    const [ow, oh] = [size[0] ?? 0, size[1] ?? 0];
    const next = layers.map((l) => {
      const data: number[] = new Array(w * h).fill(-1);
      for (let y = 0; y < Math.min(h, oh); y++)
        for (let x = 0; x < Math.min(w, ow); x++) data[y * w + x] = l.data?.[y * ow + x] ?? -1;
      return { ...l, data };
    });
    onChange({ ...value, size: [w, h], layers: next });
  };

  return (
    <div className="tilemap-editor">
      <div className="tilemap-row">
        <span className="muted">Map</span>
        <input
          className="mono tilemap-size"
          value={sizeText ?? `${size[0]}x${size[1]}`}
          onChange={(e) => setSizeText(e.target.value)}
          onBlur={() => {
            const m = sizeText?.match(/^\s*(\d+)\s*[x×, ]\s*(\d+)\s*$/i);
            if (m) resize(Math.min(4096, Number(m[1])), Math.min(4096, Number(m[2])));
            setSizeText(null);
          }}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          title="Map size in tiles (width x height)"
          aria-label="Map size"
        />
        <span className="bar-spacer" />
        <button
          type="button"
          className={`small ${painting ? 'primary' : 'ghost'}`}
          title="Paint tiles in the viewport (Shift+drag erases)"
          onClick={() => brush.set({ painting: painting ? null : entity.id, layer: Math.min(brush.layer, Math.max(0, layers.length - 1)) })}
        >
          <Icon name="pencil" size={12} /> {painting ? 'Painting' : 'Paint tiles'}
        </button>
      </div>

      <div className="tilemap-layers">
        {layers.map((l, i) => (
          <div key={i} className={`tilemap-layer${brush.layer === i ? ' is-active' : ''}`} onClick={() => brush.set({ layer: i })}>
            <input type="radio" checked={brush.layer === i} onChange={() => brush.set({ layer: i })} aria-label={`Paint on ${l.name ?? `layer ${i}`}`} />
            <input
              className="tilemap-layer-name"
              value={l.name ?? ''}
              onChange={(e) => setLayers(layers.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
              aria-label="Layer name"
            />
            <label className="tilemap-flag" title="Tiles on this layer collide">
              <input type="checkbox" checked={l.solid === true} onChange={(e) => setLayers(layers.map((x, j) => (j === i ? { ...x, solid: e.target.checked } : x)))} />
              solid
            </label>
            <IconButton
              icon={l.visible === false ? 'eyeOff' : 'eye'}
              title={l.visible === false ? 'Show layer' : 'Hide layer'}
              size={12}
              onClick={() => setLayers(layers.map((x, j) => (j === i ? { ...x, visible: x.visible === false } : x)))}
            />
            <IconButton icon="trash" title="Delete layer" size={12} disabled={layers.length <= 1} onClick={() => setLayers(layers.filter((_, j) => j !== i))} />
          </div>
        ))}
        <button type="button" className="ghost small" onClick={() => setLayers([...layers, { name: `layer${layers.length}`, data: [], solid: false }])}>
          <Icon name="plus" size={12} /> Layer
        </button>
      </div>

      <div className="tilemap-row">
        <span className="muted">Tiles</span>
        <span className="bar-spacer" />
        <label className="tilemap-flag" title="Brush size">
          Brush
          <select value={brush.size} onChange={(e) => brush.set({ size: Number(e.target.value) })}>
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                {n}×{n}
              </option>
            ))}
          </select>
        </label>
      </div>
      {error && <p className="inline-error">{error}</p>}
      {!tileset && <p className="muted tilemap-hint">Set a tileset (.pixel.json or .png) to paint with.</p>}
      <div className="tile-palette" role="listbox" aria-label="Tiles">
        <button
          type="button"
          role="option"
          aria-selected={brush.tile === -1}
          className={`tile-chip tile-erase${brush.tile === -1 ? ' is-on' : ''}`}
          title="Eraser (-1)"
          onClick={() => brush.set({ tile: -1 })}
        >
          <Icon name="eraser" size={14} />
        </button>
        {images?.tiles.map((t, i) => (
          <button
            key={i}
            type="button"
            role="option"
            aria-selected={brush.tile === i}
            className={`tile-chip${brush.tile === i ? ' is-on' : ''}`}
            title={`Tile ${i}`}
            onClick={() => brush.set({ tile: i })}
          >
            <TileThumb tile={t} />
          </button>
        ))}
      </div>
    </div>
  );
}
